import { useEffect, useRef, useState } from 'react';
import ApiDatabase from 'server';

export const RUNS_PER_PAGE = 20;

/**
 * The month's import runs, newest first, read when the journal opens
 * (JBWEB-1281), then again from the first page each time `refreshKey` changes
 * (a run starting or ending). `loadMore` adds the next page under the list.
 * As with the invoices to fix, what is shown is never blanked by a new read.
 */
const useInvoiceImportRuns = (period, refreshKey) => {
  const [state, setState] = useState({ status: 'loading', data: null });
  const [attempt, setAttempt] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const hasData = useRef(false);
  // Counts the reads of the first page: an answer to an older one is dropped,
  // and so is a next page asked before the first page was read again.
  const firstPageRead = useRef(0);

  useEffect(() => {
    const read = ++firstPageRead.current;
    if (!hasData.current) setState({ status: 'loading', data: null });
    setIsLoadingMore(false);
    ApiDatabase.getInvoiceImportRuns(
      { period, page: 1, limit: RUNS_PER_PAGE },
      (data) => {
        if (read !== firstPageRead.current) return;
        hasData.current = true;
        setState({ status: 'ready', data });
      },
      (error) => {
        if (read !== firstPageRead.current) return;
        if (error?.name === 'CanceledError' || hasData.current) return;
        setState({ status: 'failed', data: null });
      },
    );
    return () => {
      firstPageRead.current += 1;
    };
  }, [period, refreshKey, attempt]);

  const loadMore = () => {
    const read = firstPageRead.current;
    setIsLoadingMore(true);
    ApiDatabase.getInvoiceImportRuns(
      { period, page: state.data.page + 1, limit: RUNS_PER_PAGE },
      (data) => {
        if (read !== firstPageRead.current) return;
        setIsLoadingMore(false);
        // A run started meanwhile shifts the pages: a run already shown is not shown twice.
        setState((previous) => {
          const shownIds = new Set(previous.data.runs.map((run) => run.id));
          return {
            status: 'ready',
            data: {
              ...data,
              runs: [
                ...previous.data.runs,
                ...data.runs.filter((run) => !shownIds.has(run.id)),
              ],
            },
          };
        });
      },
      (error) => {
        if (read !== firstPageRead.current) return;
        // Cancelled or failed, the button stays: another click asks again.
        setIsLoadingMore(false);
      },
      // Its own abort key: a next page and a new read of the first page do not cancel each other.
      { abortKey: 'admin/invoices/imports?more' },
    );
  };

  return {
    ...state,
    isLoadingMore,
    loadMore,
    reload: () => setAttempt((count) => count + 1),
  };
};

export default useInvoiceImportRuns;
